import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Icon, Reveal, Stat } from "../ui/primitives"
import { useTC } from "../TrustCenterContext"
import darwinboxLogo from "../../assets/darwinbox-logo.png"
import darwinboxLogoWhite from "../../assets/darwinbox-logo-white.png"

function ProfileDropdown({ user, onLogout, onAdmin }) {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef(null)

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const initials = `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`.toUpperCase() || user.email?.[0]?.toUpperCase() || "U"
    const displayName = user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.email

    return (
        <div ref={dropdownRef} style={{ position: "relative" }}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "6px 12px 6px 6px",
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "24px",
                    cursor: "pointer",
                    transition: "all 0.18s",
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.14)"
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.background = "rgba(255,255,255,0.08)"
                    e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"
                }}
            >
                <div style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #0183FF, #5348D7)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: "700",
                    color: "#fff",
                }}>
                    {initials}
                </div>
                <span style={{
                    color: "rgba(255,255,255,0.9)",
                    fontSize: "13px",
                    fontWeight: "500",
                    maxWidth: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}>
                    {displayName}
                </span>
                <Icon name="chevron-down" size={14} style={{
                    color: "rgba(255,255,255,0.6)",
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.2s",
                }} />
            </button>

            {isOpen && (
                <div style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    minWidth: "200px",
                    background: "#fff",
                    borderRadius: "12px",
                    boxShadow: "0 10px 40px -8px rgba(0,0,0,0.3)",
                    border: "1px solid rgba(0,0,0,0.08)",
                    overflow: "hidden",
                    zIndex: 100,
                    animation: "dropdownFade 0.15s ease-out",
                }}>
                    <div style={{
                        padding: "14px 16px",
                        borderBottom: "1px solid #eee",
                    }}>
                        <div style={{ fontSize: "14px", fontWeight: "600", color: "#002B54" }}>
                            {displayName}
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                            {user.email}
                        </div>
                        {user.company && (
                            <div style={{ fontSize: "11px", color: "#94a0b0", marginTop: "4px" }}>
                                {user.company}
                            </div>
                        )}
                    </div>

                    <div style={{ padding: "6px" }}>
                        <Link
                            to="/profile"
                            onClick={() => setIsOpen(false)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                padding: "10px 12px",
                                borderRadius: "8px",
                                color: "#334155",
                                fontSize: "13px",
                                fontWeight: "500",
                                textDecoration: "none",
                                transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#f1f5f9"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                            Profile Settings
                        </Link>

                        {onAdmin && (
                            <button
                                onClick={() => { setIsOpen(false); onAdmin() }}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    width: "100%",
                                    padding: "10px 12px",
                                    borderRadius: "8px",
                                    background: "transparent",
                                    border: "none",
                                    color: "#334155",
                                    fontSize: "13px",
                                    fontWeight: "500",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    transition: "background 0.15s",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = "#f1f5f9"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >
                                <Icon name="gear" size={16} style={{ color: "#64748b" }} />
                                Admin Dashboard
                            </button>
                        )}
                    </div>

                    <div style={{ padding: "6px", borderTop: "1px solid #eee" }}>
                        <button
                            onClick={() => { setIsOpen(false); onLogout() }}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                width: "100%",
                                padding: "10px 12px",
                                borderRadius: "8px",
                                background: "transparent",
                                border: "none",
                                color: "#dc2626",
                                fontSize: "13px",
                                fontWeight: "500",
                                cursor: "pointer",
                                textAlign: "left",
                                transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#fef2f2"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                            <Icon name="arrow-right" size={16} style={{ color: "#dc2626", transform: "rotate(180deg)" }} />
                            Sign Out
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes dropdownFade {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    )
}

export const BASE_NAV = [
    { id: "overview", label: "Overview" },
    { id: "controls", label: "Controls" },
    { id: "documents", label: "Documents" },
    { id: "subprocessors", label: "Subprocessors", widget: "Subprocessors" },
    { id: "faq", label: "FAQ", widget: "FAQ" },
]

function Brand({ onClick }) {
    return (
        <div className="brand" onClick={onClick}>
            <img src={darwinboxLogo} alt="Darwinbox" className="brand__logo" />
            <span className="brand__badge">Trust Center</span>
        </div>
    )
}

export function Header({ view, go, nav, user, onLogin, onLogout, onAdmin }) {
    const [scrolled, setScrolled] = useState(false)
    const navRef = useRef(null)
    const [indicator, setIndicator] = useState({ left: 0, width: 0, ready: false })

    const updateIndicator = useCallback(() => {
        const navEl = navRef.current
        if (!navEl) return
        const btn = navEl.querySelector(`button[data-nav-id="${view}"]`)
        if (!btn) return
        setIndicator({
            left: btn.offsetLeft,
            width: btn.offsetWidth,
            ready: true,
        })
    }, [view])

    useEffect(() => {
        const f = () => setScrolled(window.scrollY > 8)
        f()
        window.addEventListener("scroll", f, { passive: true })
        return () => window.removeEventListener("scroll", f)
    }, [])

    useLayoutEffect(() => {
        updateIndicator()
    }, [updateIndicator, nav])

    useEffect(() => {
        window.addEventListener("resize", updateIndicator)
        return () => window.removeEventListener("resize", updateIndicator)
    }, [updateIndicator])

    return (
        <header className={"tc-header" + (scrolled ? " scrolled" : "")}>
            <div className="tc-header__in">
                <Brand onClick={() => go("overview")} />
                <nav className="tc-nav" ref={navRef}>
                    <span
                        className={"tc-nav__indicator" + (indicator.ready ? " tc-nav__indicator--ready" : "")}
                        style={{ transform: `translateX(${indicator.left}px)`, width: indicator.width }}
                        aria-hidden="true"
                    />
                    {nav.map((n) => (
                        <button
                            key={n.id}
                            type="button"
                            data-nav-id={n.id}
                            className={view === n.id ? "active" : ""}
                            onClick={() => go(n.id)}
                        >
                            {n.label}
                        </button>
                    ))}
                </nav>
                <div className="tc-header__cta">
                    {user ? (
                        <ProfileDropdown user={user} onLogout={onLogout} onAdmin={onAdmin} />
                    ) : (
                        <button className="btn btn--accent btn--sm" onClick={onLogin}><Icon name="padlock-locked" size={16} />Login</button>
                    )}
                </div>
            </div>
        </header>
    )
}

function TrustOrbit() {
    const D = useTC()
    return (
        <div className="trust-orbit">
            <span className="to-ring to-ring--1" />
            <span className="to-ring to-ring--2" />
            <span className="to-ring to-ring--3" />
            <span className="to-sweep" />
            {D.orbitIcons.map((n) => (
                <span key={n.ring} className={"to-orbit to-orbit--" + n.ring}>
                    <span className="to-node" aria-hidden="true">
                        <span className="to-node__i"><Icon name={n.icon} size={20} /></span>
                    </span>
                </span>
            ))}
            <div className="to-core">
                <span className="to-core__pulse" />
                <span className="to-core__pulse to-core__pulse--2" />
                <span className="to-core__disc"><Icon name="shield-check" size={42} /></span>
            </div>
            <div className="to-cap"><span className="to-cap__dot" />Monitored Realtime</div>
        </div>
    )
}

export function Hero() {
    const D = useTC()
    return (
        <section className="hero">
            <div className="hero__grid" />
            <div className="hero__orb hero__orb--1" />
            <div className="hero__orb hero__orb--2" />
            <div className={"shell hero__layout" + (D.showOrbit ? "" : " hero__layout--no-orbit")}>
                <div className="hero__in">
                    <Reveal>
                        <h1>Security and Trust,<br /><span className="grad">Built for the Enterprise.</span></h1>
                    </Reveal>
                    <Reveal delay={80}>
                        <p className="hero__lede">Learn how Darwinbox protects the people data of 3 million+ employees across 130+ countries — our certifications, controls and commitments, all in one place.</p>
                    </Reveal>
                    <Reveal delay={150}>
                        <div className="hero__stats">
                            {D.stats.map((s) => <Stat key={s.label} {...s} />)}
                        </div>
                    </Reveal>
                </div>
                {D.showOrbit && <TrustOrbit />}
            </div>
        </section>
    )
}

export function Footer() {
    return (
        <footer className="tc-footer">
            <div className="tc-footer__grid" />
            <span className="tc-footer__orb" />
            <div className="shell">
                <div className="tc-footer__top">
                    <div style={{ maxWidth: 300, display: "flex", flexDirection: "column" }}>
                        <img src={darwinboxLogoWhite} alt="Darwinbox" className="brand__logo brand__logo--footer" />
                        <p style={{ margin: 0, fontSize: "var(--font-size-body-s)", lineHeight: 1.6, width: "600px" }}>The AI-native HCM platform trusted by the world&apos;s largest enterprises.</p>
                        <p className="tc-footer__copy">© {new Date().getFullYear()} Darwinbox. All rights reserved.</p>
                    </div>
                </div>
                <div className="tc-footer__legal">
                    <a href="https://darwinbox.com/sub-processors" target="_blank" rel="noreferrer">Subprocessors</a>
                    <span className="tc-footer__sep" aria-hidden="true">|</span>
                    <a href="https://darwinbox.com" target="_blank" rel="noreferrer">darwinbox.com</a>
                    <span className="tc-footer__sep" aria-hidden="true">|</span>
                    <a href="https://darwinbox.com/privacy-policy" target="_blank" rel="noreferrer">Privacy policy</a>
                </div>
            </div>
        </footer>
    )
}

export function useAdminPath(user) {
    const navigate = useNavigate()
    return () => {
        if (!user || user.role !== "admin") return
        const perms = user.permissions || {}
        const first = [
            { path: "/admin", show: perms.approveRequests },
            { path: "/widgets", show: perms.manageWidgets },
            { path: "/admins", show: perms.manageAdmins },
            { path: "/upload", show: perms.uploadDocs },
        ].find((item) => item.show)
        if (first) navigate(first.path)
    }
}
